class FooterTransition {
  constructor(element) {
    this.element = element;
    this.transitionTarget = element.closest('.footer-wrapper.footer-group') || element;
    this.anchor = document.querySelector('#MainContent');
    this.shadow = this.anchor?.querySelector('.footer-transition-shadow');
    this.desktopMedia = window.matchMedia('(min-width: 768px)');
    this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.useNativeMobileTimeline = Boolean(
      window.CSS?.supports?.('animation-timeline', 'view()') &&
      window.CSS.supports('animation-range', 'entry') &&
      window.CSS.supports('timeline-scope', '--lbr-footer-reveal')
    );
    this.update = this.update.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleMobileAnimationEnd = this.handleMobileAnimationEnd.bind(this);
    this.frame = null;
    this.mobileFrame = null;
    this.resizeObserver = null;
    this.mobileObserver = null;
    this.layoutTop = 0;
    this.isDesktop = this.desktopMedia.matches;
  }

  connect() {
    if (this.desktopMedia.addEventListener) {
      this.desktopMedia.addEventListener('change', this.handleBreakpointChange);
    } else {
      this.desktopMedia.addListener(this.handleBreakpointChange);
    }

    this.setupMode();
  }

  disconnect() {
    this.teardownMode();

    if (this.desktopMedia.removeEventListener) {
      this.desktopMedia.removeEventListener('change', this.handleBreakpointChange);
    } else {
      this.desktopMedia.removeListener(this.handleBreakpointChange);
    }
  }

  setupMode() {
    this.teardownMode();
    this.isDesktop = this.desktopMedia.matches;

    if (this.reducedMotionMedia.matches) {
      this.resetTransitionStyles();
      return;
    }

    if (this.isDesktop) {
      this.setupDesktop();
    } else {
      this.setupMobile();
    }
  }

  teardownMode() {
    window.removeEventListener('scroll', this.update);
    window.removeEventListener('resize', this.handleResize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mobileObserver?.disconnect();
    this.mobileObserver = null;

    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.mobileFrame) cancelAnimationFrame(this.mobileFrame);
    this.frame = null;
    this.mobileFrame = null;

    this.transitionTarget.removeEventListener('animationend', this.handleMobileAnimationEnd);
    this.transitionTarget.classList.remove(
      'footer-transition--mobile-ready',
      'footer-transition--mobile-revealing',
      'footer-transition--mobile-revealed',
      'footer-transition--mobile-native'
    );
    this.shadow?.classList.remove(
      'footer-transition-shadow--mobile-ready',
      'footer-transition-shadow--mobile-revealing',
      'footer-transition-shadow--mobile-revealed',
      'footer-transition-shadow--mobile-native'
    );
  }

  resetTransitionStyles() {
    this.transitionTarget.style.removeProperty('transform');
    this.transitionTarget.style.removeProperty('--footer-transition-shadow-opacity');
    this.shadow?.style.removeProperty('opacity');
  }

  setupDesktop() {
    this.resetTransitionStyles();
    this.measure();
    window.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.handleResize);

    if (this.anchor && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.update();
      });
      this.resizeObserver.observe(this.anchor);
    }

    this.update();
  }

  setupMobile() {
    this.resetTransitionStyles();

    if (this.useNativeMobileTimeline) {
      this.transitionTarget.classList.add('footer-transition--mobile-native');
      this.shadow?.classList.add('footer-transition-shadow--mobile-native');
      return;
    }

    this.transitionTarget.classList.add('footer-transition--mobile-ready');
    this.shadow?.classList.add('footer-transition-shadow--mobile-ready');

    if (!('IntersectionObserver' in window)) {
      this.handleMobileAnimationEnd();
      return;
    }

    this.mobileObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.mobileObserver?.disconnect();
        this.mobileObserver = null;
        this.mobileFrame = requestAnimationFrame(() => {
          this.mobileFrame = null;
          this.transitionTarget.classList.add('footer-transition--mobile-revealing');
          this.shadow?.classList.add('footer-transition-shadow--mobile-revealing');
          this.transitionTarget.addEventListener('animationend', this.handleMobileAnimationEnd);
        });
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.01,
      }
    );

    this.mobileObserver.observe(this.transitionTarget);
  }

  handleMobileAnimationEnd(event) {
    if (
      event &&
      (event.target !== this.transitionTarget || event.animationName !== 'lbr-footer-mobile-reveal')
    ) {
      return;
    }

    this.transitionTarget.removeEventListener('animationend', this.handleMobileAnimationEnd);
    this.transitionTarget.classList.remove(
      'footer-transition--mobile-ready',
      'footer-transition--mobile-revealing'
    );
    this.transitionTarget.classList.add('footer-transition--mobile-revealed');
    this.shadow?.classList.remove(
      'footer-transition-shadow--mobile-ready',
      'footer-transition-shadow--mobile-revealing'
    );
    this.shadow?.classList.add('footer-transition-shadow--mobile-revealed');
  }

  handleBreakpointChange() {
    this.setupMode();
  }

  measure() {
    const anchorRect = this.anchor?.getBoundingClientRect();
    this.layoutTop = anchorRect
      ? anchorRect.bottom + window.scrollY
      : this.transitionTarget.getBoundingClientRect().top + window.scrollY;
    this.maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    this.footerHeight = this.element.offsetHeight;
  }

  handleResize() {
    this.measure();
    this.update();
  }

  update() {
    if (!this.isDesktop || this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const revealStart = Math.max(0, this.layoutTop - window.innerHeight);
      const revealRange = Math.max(1, this.maxScroll - revealStart);
      const progress = Math.max(0, Math.min(1, (window.scrollY - revealStart) / revealRange));
      const offset = (progress - 1) * this.footerHeight * 0.5;
      const shadowOpacity = (1 - progress) * 0.95;

      this.transitionTarget.style.transform = `translateY(${offset}px)`;
      this.transitionTarget.style.setProperty('--footer-transition-shadow-opacity', shadowOpacity.toFixed(3));
      if (this.shadow) this.shadow.style.opacity = shadowOpacity.toFixed(3);
    });
  }
}

const footerTransitions = new WeakMap();

const initializeFooterTransitions = () => {
  document.querySelectorAll('[data-footer-transition].footer-main-transition--enabled').forEach((element) => {
    if (footerTransitions.has(element)) return;

    const transition = new FooterTransition(element);
    transition.connect();
    footerTransitions.set(element, transition);
  });
};

initializeFooterTransitions();
document.addEventListener('shopify:section:load', initializeFooterTransitions);
